import { apiBaseUrl, bearerHeaders, pb } from "./pocketbase";
import type {
  BundleManifest,
  EegRequestMetrics,
  EegWindow,
  ParticipantDetailV1,
  ParticipantIndexV1,
  ProcessingJob,
  RecordingIndexV1,
  RecordingCapabilitiesV1,
  SignalIndexV1,
  SignalProfileV1,
  SignalOverviewV1,
  SignalWindowV1,
  WorkspaceReleaseSummary,
  WorkspaceSummary,
} from "@rekonas/domain";
import { authCacheScope, authDataIdentity } from "./cacheScope";
import {
  parseBundleManifest,
  parseParticipantDetail,
  parseParticipantIndex,
  parseRecordingIndex,
  parseRecordingCapabilities,
  parseSignalOverview,
  parseSignalProfile,
  parseSignalWindow,
} from "./sponsorData";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function endpoint(path: string): string {
  return new URL(path, `${apiBaseUrl}/`).toString();
}

const responseBoundaries = new WeakMap<Response, string>();
function checkResponseBoundary(response: Response): void {
  const identity=responseBoundaries.get(response);
  if (identity !== undefined && identity !== authDataIdentity()) throw new DOMException("Authorization scope changed", "AbortError");
}

export async function authorizedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const identity=authDataIdentity(), token=pb.authStore.token;
  const headers = new Headers(init.headers);
  const authHeaders = bearerHeaders();
  Object.entries(authHeaders).forEach(([name, value]) => headers.set(name, value));
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(endpoint(path), { ...init, headers });
  if (identity !== authDataIdentity() || init.signal?.aborted) throw new DOMException("Authorization scope changed", "AbortError");
  responseBoundaries.set(response,identity);
  if (response.status === 401) {
    if (token !== pb.authStore.token) throw new DOMException("Credentials refreshed", "AbortError");
    pb.authStore.clear();
    window.dispatchEvent(new Event("pb-auth-invalid"));
  }
  return response;
}

export async function parseResponse<T>(response: Response): Promise<T> {
  const raw = await response.text();
  checkResponseBoundary(response);
  let body: unknown;
  try {
    body = raw ? JSON.parse(raw) : undefined;
  } catch {
    body = raw;
  }
  if (!response.ok) {
    const serverMessage =
      typeof body === "object" && body
        ? "message" in body && typeof body.message === "string"
          ? body.message
          : "detail" in body && typeof body.detail === "string"
            ? body.detail
            : undefined
        : undefined;
    throw new ApiError(serverMessage || `Request failed (${response.status})`, response.status, body);
  }
  return body as T;
}

export const workspaceKeys = {
  all: () => ["workspaces", authCacheScope()] as const,
  detail: (id: string) => ["workspaces", authCacheScope(), id] as const,
  activeRelease: (id: string) =>
    ["workspaces", authCacheScope(), id, "active-release"] as const,
  release: (id: string, releaseId: string) =>
    ["workspaces", authCacheScope(), id, "releases", releaseId] as const,
  bundle: (url: string) => ["workspace-bundle", authCacheScope(), url] as const,
};

export const participantKeys = {
  all: () => ["participants", authCacheScope()] as const,
  index: (workspaceId: string, releaseId: string) =>
    ["participants", authCacheScope(), workspaceId, releaseId, "index"] as const,
  detail: (workspaceId: string, releaseId: string, participantRef: string) =>
    ["participants", authCacheScope(), workspaceId, releaseId, participantRef, "detail"] as const,
  recordings: (workspaceId: string, releaseId: string, participantRef: string) =>
    ["participants", authCacheScope(), workspaceId, releaseId, participantRef, "recordings"] as const,
  recordingAvailability: (
    workspaceId: string,
    releaseId: string,
    participantRef: string,
    recordingRef: string,
  ) => [
    "participants",
    authCacheScope(),
    workspaceId,
    releaseId,
    participantRef,
    "recordings",
    recordingRef,
    "availability",
  ] as const,
};

export const sponsorSignalKeys = {
  scope: (workspaceId: string, releaseId: string, participantRef: string, recordingRef: string) => [
    "sponsor-signals",
    authCacheScope(),
    workspaceId,
    releaseId,
    participantRef,
    recordingRef,
  ] as const,
  capabilities: (workspaceId: string, releaseId: string, participantRef: string, recordingRef: string) => [
    ...sponsorSignalKeys.scope(workspaceId, releaseId, participantRef, recordingRef),
    "capabilities",
  ] as const,
  overview: (
    workspaceId: string,
    releaseId: string,
    participantRef: string,
    recordingRef: string,
    profileSha256: string,
    dataVersion: string,
    modalities: string[],
    maxPoints: number,
  ) => [
    ...sponsorSignalKeys.scope(workspaceId, releaseId, participantRef, recordingRef),
    "overview",
    profileSha256,
    dataVersion,
    [...modalities].sort().join(","),
    maxPoints,
  ] as const,
  window: (
    workspaceId: string,
    releaseId: string,
    participantRef: string,
    recordingRef: string,
    profileSha256: string,
    dataVersion: string,
    modalities: string[],
    start: number,
    duration: number,
  ) => [
    ...sponsorSignalKeys.scope(workspaceId, releaseId, participantRef, recordingRef),
    "window",
    profileSha256,
    dataVersion,
    [...modalities].sort().join(","),
    start,
    duration,
  ] as const,
};

function participantRoute(
  workspaceId: string,
  releaseId: string,
  suffix = "",
): string {
  return (
    `/platform/v1/workspaces/${encodeURIComponent(workspaceId)}/releases/` +
    `${encodeURIComponent(releaseId)}/participants${suffix}`
  );
}

export async function listWorkspaces(signal?: AbortSignal): Promise<WorkspaceSummary[]> {
  return parseResponse<WorkspaceSummary[]>(
    await authorizedFetch("/platform/v1/workspaces", { signal }),
  );
}

export async function getWorkspace(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<WorkspaceSummary> {
  return parseResponse<WorkspaceSummary>(
    await authorizedFetch(`/platform/v1/workspaces/${encodeURIComponent(workspaceId)}`, { signal }),
  );
}

export async function getActiveWorkspaceRelease(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<WorkspaceReleaseSummary> {
  return parseResponse<WorkspaceReleaseSummary>(
    await authorizedFetch(
      `/platform/v1/workspaces/${encodeURIComponent(workspaceId)}/active-release`,
      { signal },
    ),
  );
}

export async function getWorkspaceRelease(
  workspaceId: string,
  releaseId: string,
  signal?: AbortSignal,
): Promise<WorkspaceReleaseSummary> {
  return parseResponse<WorkspaceReleaseSummary>(
    await authorizedFetch(
      `/platform/v1/workspaces/${encodeURIComponent(workspaceId)}/releases/${encodeURIComponent(releaseId)}`,
      { signal },
    ),
  );
}

export async function fetchBundleJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  return parseResponse<T>(await authorizedFetch(url, { signal }));
}

export async function fetchBundleManifest(
  release: WorkspaceReleaseSummary,
  signal?: AbortSignal,
): Promise<BundleManifest> {
  return parseBundleManifest(await fetchBundleJson<unknown>(release.manifest_url, signal));
}

export function fetchSignalIndex(url: string, signal?: AbortSignal): Promise<SignalIndexV1> {
  return fetchBundleJson<SignalIndexV1>(url, signal);
}

export async function fetchParticipantIndex(
  workspaceId: string,
  releaseId: string,
  signal?: AbortSignal,
): Promise<ParticipantIndexV1> {
  return parseParticipantIndex(
    await parseResponse<unknown>(
      await authorizedFetch(participantRoute(workspaceId, releaseId), { signal }),
    ),
  );
}

export async function fetchParticipantDetail(
  workspaceId: string,
  releaseId: string,
  participantRef: string,
  signal?: AbortSignal,
): Promise<ParticipantDetailV1> {
  return parseParticipantDetail(
    await parseResponse<unknown>(
      await authorizedFetch(
        participantRoute(
          workspaceId,
          releaseId,
          `/${encodeURIComponent(participantRef)}`,
        ),
        { signal },
      ),
    ),
  );
}

export async function fetchParticipantRecordings(
  workspaceId: string,
  releaseId: string,
  participantRef: string,
  signal?: AbortSignal,
): Promise<RecordingIndexV1> {
  return parseRecordingIndex(
    await parseResponse<unknown>(
      await authorizedFetch(
        participantRoute(
          workspaceId,
          releaseId,
          `/${encodeURIComponent(participantRef)}/recordings`,
        ),
        { signal },
      ),
    ),
  );
}

export async function resolveRecordingAvailability(
  workspaceId: string,
  recordingRef: string,
  signal?: AbortSignal,
): Promise<boolean> {
  const value = await parseResponse<unknown>(
    await authorizedFetch(
      `/platform/v1/workspaces/${encodeURIComponent(workspaceId)}/recording-references/${encodeURIComponent(recordingRef)}`,
      { signal },
    ),
  );
  if (
    typeof value !== "object" ||
    value === null ||
    Array.isArray(value) ||
    Object.keys(value).some((key) => !["recording_ref", "recording_id"].includes(key)) ||
    (value as Record<string, unknown>).recording_ref !== recordingRef ||
    typeof (value as Record<string, unknown>).recording_id !== "string" ||
    !(value as Record<string, unknown>).recording_id
  ) {
    throw new TypeError("Invalid recording availability response");
  }
  return true;
}

export async function fetchSignalProfile(
  url: string,
  signal?: AbortSignal,
): Promise<SignalProfileV1> {
  return parseSignalProfile(await fetchBundleJson<unknown>(url, signal));
}

function sponsorRecordingRoute(
  workspaceId: string,
  releaseId: string,
  recordingRef: string,
  resource: string,
): string {
  return (
    `/platform/v1/workspaces/${encodeURIComponent(workspaceId)}/releases/` +
    `${encodeURIComponent(releaseId)}/recordings/${encodeURIComponent(recordingRef)}/${resource}`
  );
}

export async function fetchRecordingCapabilities(
  workspaceId: string,
  releaseId: string,
  recordingRef: string,
  signal?: AbortSignal,
): Promise<RecordingCapabilitiesV1> {
  return parseRecordingCapabilities(
    await parseResponse<unknown>(
      await authorizedFetch(
        sponsorRecordingRoute(workspaceId, releaseId, recordingRef, "capabilities"),
        { signal },
      ),
    ),
  );
}

export async function fetchSignalOverview(params: {
  workspaceId: string;
  releaseId: string;
  recordingRef: string;
  modalities: string[];
  maxPoints?: number;
  signal?: AbortSignal;
}): Promise<SignalOverviewV1> {
  const search = new URLSearchParams();
  if (params.modalities.length) search.set("modalities", params.modalities.join(","));
  search.set("max_points", String(params.maxPoints ?? 8_000));
  return parseSignalOverview(
    await parseResponse<unknown>(
      await authorizedFetch(
        `${sponsorRecordingRoute(params.workspaceId, params.releaseId, params.recordingRef, "overview")}?${search}`,
        { signal: params.signal },
      ),
    ),
  );
}

export async function fetchSignalWindow(params: {
  workspaceId: string;
  releaseId: string;
  recordingRef: string;
  startSeconds: number;
  durationSeconds: 15 | 30 | 60;
  modalities: string[];
  maxPoints?: number;
  signal?: AbortSignal;
}): Promise<SignalWindowV1> {
  const search = new URLSearchParams({
    start_seconds: String(params.startSeconds),
    duration_seconds: String(params.durationSeconds),
    max_points: String(params.maxPoints ?? 100_000),
  });
  if (params.modalities.length) search.set("modalities", params.modalities.join(","));
  return parseSignalWindow(
    await parseResponse<unknown>(
      await authorizedFetch(
        `${sponsorRecordingRoute(params.workspaceId, params.releaseId, params.recordingRef, "window")}?${search}`,
        { signal: params.signal },
      ),
    ),
  );
}

export async function startHypnogram(
  recordingId: string,
  options: { force?: boolean; signal?: AbortSignal } = {},
): Promise<ProcessingJob> {
  const response = await authorizedFetch(`/api/recordings/${encodeURIComponent(recordingId)}/hypnogram`, {
    method: "POST",
    body: JSON.stringify({ force: Boolean(options.force) }),
    signal: options.signal,
  });
  return parseResponse<ProcessingJob>(response);
}

export async function getJob(jobId: string, signal?: AbortSignal): Promise<ProcessingJob> {
  const response = await authorizedFetch(`/api/jobs/${encodeURIComponent(jobId)}`, { signal });
  return parseResponse<ProcessingJob>(response);
}

export async function fetchEegWindow(params: {
  recordingId: string;
  start: number;
  duration: number;
  channels?: string[];
  signal?: AbortSignal;
}): Promise<EegWindow> {
  const search = new URLSearchParams({
    start: String(params.start),
    duration: String(params.duration),
  });
  if (params.channels?.length) search.set("channels", params.channels.join(","));

  const started = performance.now();
  const response = await authorizedFetch(
    `/data/recordings/${encodeURIComponent(params.recordingId)}/eeg?${search.toString()}`,
    { signal: params.signal },
  );
  const finished = performance.now();
  const raw = await response.text();
  checkResponseBoundary(response);
  const decompressedBytes = new TextEncoder().encode(raw).byteLength;
  let body: unknown;
  try {
    body = raw ? JSON.parse(raw) : undefined;
  } catch {
    body = raw;
  }
  if (!response.ok) {
    const message =
      typeof body === "object" && body
        ? "message" in body && typeof body.message === "string"
          ? body.message
          : "detail" in body && typeof body.detail === "string"
            ? body.detail
            : `EEG slice request failed (${response.status})`
        : `EEG slice request failed (${response.status})`;
    throw new ApiError(message, response.status, body);
  }

  const payload = (body || {}) as Record<string, unknown>;
  const serverMetrics = ((payload.metrics || {}) as EegRequestMetrics);
  const wireBytes = Number(response.headers.get("content-length"));
  const canonicalChannels = Array.isArray(payload.channels) ? payload.channels : [];
  const values = Array.isArray(payload.values) ? payload.values : [];
  const channels = canonicalChannels.map((channel, index) => {
    const metadata = channel && typeof channel === "object" ? (channel as Record<string, unknown>) : {};
    const embeddedSamples = Array.isArray(metadata.samples) ? metadata.samples : undefined;
    const rawSamples = embeddedSamples || (Array.isArray(values[index]) ? values[index] : []);
    return {
      label: String(metadata.label || `channel-${index + 1}`),
      unit: String(metadata.unit || metadata.physical_unit || "unknown"),
      samples: rawSamples.map((sample) => (typeof sample === "number" ? sample : Number.NaN)),
    };
  });
  const payloadStart = payload.start ?? payload.start_seconds;
  const payloadDuration = payload.duration ?? payload.duration_seconds;
  const payloadRate = payload.sample_rate ?? payload.sample_rate_hz;
  return {
    start: typeof payloadStart === "number" ? payloadStart : params.start,
    duration: typeof payloadDuration === "number" ? payloadDuration : params.duration,
    sample_rate: typeof payloadRate === "number" ? payloadRate : 0,
    channels,
    time_origin: typeof payload.time_origin === "string" ? payload.time_origin : undefined,
    metrics: {
      ...serverMetrics,
      request_ms: Math.round((finished - started) * 10) / 10,
      bytes:
        typeof serverMetrics.transferred_bytes === "number"
          ? serverMetrics.transferred_bytes
          : Number.isFinite(wireBytes)
            ? wireBytes
            : serverMetrics.bytes,
      decompressed_bytes:
        typeof serverMetrics.decoded_bytes === "number" ? serverMetrics.decoded_bytes : decompressedBytes,
    },
  };
}

export async function fetchPositionDistribution(params: { workspaceId: string; releaseId: string; recordingRef: string; signal?: AbortSignal }) {
  const { fetchPositionDistributionPath } = await import("./positionDistribution");
  return fetchPositionDistributionPath(sponsorRecordingRoute(params.workspaceId, params.releaseId, params.recordingRef, "position-distribution"), params.signal);
}
